import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Building2, Trash2, RefreshCw } from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDateTime } from '@/utils/formatters';
import {
  roleLabels,
  roleClasses,
  ASSINATURA_LABEL,
  ASSINATURA_CLASS,
  STATUS_COBRE,
} from './userDisplay';
import type { User } from '@/types/user.types';

// Cargos disponíveis (mesma lista usada no fluxo de criação de usuário)
const CARGO_OPTIONS = [
  'Proprietário',
  'Gestor',
  'Capataz',
  'Vaqueiro',
  'Ordenhador',
  'Salgueiro',
  'Técnico',
  'Veterinário',
] as const;

// Campo = well `muted` (distinto do drawer `elevated`) + borda forte -> destaca do fundo.
const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-4 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

const labelClass = 'mb-2 block text-sm font-medium text-text-title';

// Label caixa-alta pequena p/ título de seção do form (ex.: "INFORMAÇÕES PESSOAIS")
const sectionLabelClass = 'mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary';

export interface EditUserFormData {
  name: string;
  phone: string;
  cargo: string;
  property: {
    name: string;
    city: string;
    uf: string;
    area_total: number;
    reserva_legal: number;
    sistema_ilpf: number;
    pastagem_ha: number;
    area_agricola: number;
  };
}

interface EditUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | undefined;
  formData: EditUserFormData;
  onFormDataChange: (data: EditUserFormData) => void;
  onSave: () => void;
  onDelete: () => void;
  isSaving?: boolean;
}

/**
 * Drawer lateral de edição de usuário — 2 colunas:
 * esquerda = contexto read-only (avatar, status, cargo, acessos, assinatura);
 * direita = formulário editável (dados pessoais + propriedade + áreas).
 */
export function EditUserDrawer({
  isOpen,
  onClose,
  user,
  formData,
  onFormDataChange,
  onSave,
  onDelete,
  isSaving = false,
}: EditUserDrawerProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSaving]);

  if (!isOpen || !user) return null;

  const areasTotal =
    (formData.property.pastagem_ha || 0) +
    (formData.property.reserva_legal || 0) +
    (formData.property.area_agricola || 0) +
    (formData.property.sistema_ilpf || 0);

  const updateProperty = (patch: Partial<EditUserFormData['property']>) => {
    onFormDataChange({ ...formData, property: { ...formData.property, ...patch } });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Editar Usuário"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/70"
        onClick={() => !isSaving && onClose()}
      />

      {/* Drawer */}
      <div className="relative z-10 flex h-full w-full max-w-5xl animate-slide-in-right flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] border-l border-border bg-bg-elevated shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-text-title">Editar Usuário</h2>
          <button
            onClick={() => !isSaving && onClose()}
            aria-label="Fechar"
            className="rounded-lg p-1 text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — 2 colunas */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Coluna esquerda — contexto (read-only) */}
          <aside className="flex w-full max-w-[280px] flex-col border-r border-border bg-muted/30 p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div
                className={`flex size-16 shrink-0 items-center justify-center rounded-full text-base font-bold tracking-tight ${getAvatarColor(
                  user.id
                )}`}
              >
                {getUserInitials(user.name)}
              </div>
              <div>
                <p className="font-semibold text-text-title">{user.name}</p>
                <p className="text-sm text-text-secondary">{user.phone}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <StatusBadge status={user.status} />
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                    roleClasses[user.role] || 'bg-muted text-text-secondary'
                  }`}
                >
                  {roleLabels[user.role] || user.cargo || user.role}
                </span>
              </div>
            </div>

            <div className="my-5 border-t border-border" />

            <dl className="flex flex-col gap-4 text-sm">
              <div>
                <dt className="mb-1 font-medium text-text-secondary">Último acesso</dt>
                <dd className="text-text-title">
                  {formatDateTime(user.lastAccess)}
                  {user.lastDevice ? ` · ${user.lastDevice}` : ''}
                </dd>
              </div>

              <div>
                <dt className="mb-1 font-medium text-text-secondary">Verificação</dt>
                <dd
                  className={user.verified ? 'font-medium text-success' : 'font-medium text-error'}
                >
                  {user.verified ? 'Verificado' : 'Não verificado'}
                </dd>
              </div>

              <div>
                <dt className="mb-1 font-medium text-text-secondary">Assinatura</dt>
                <dd className="text-text-title">
                  {user.isProducer && user.assinatura ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                        ASSINATURA_CLASS[user.assinatura.status] ?? ASSINATURA_CLASS.none
                      }`}
                    >
                      {ASSINATURA_LABEL[user.assinatura.status] ?? user.assinatura.status}
                      {user.assinatura.plano?.nome ? ` · ${user.assinatura.plano.nome}` : ''}
                    </span>
                  ) : user.coberturaHerdada && STATUS_COBRE.has(user.coberturaHerdada.status) ? (
                    <span className="inline-flex items-center rounded-full bg-info/10 px-2.5 py-1 text-xs font-medium text-info">
                      Coberto
                      {user.coberturaHerdada.plano?.nome
                        ? ` · ${user.coberturaHerdada.plano.nome}`
                        : ''}
                    </span>
                  ) : (
                    <span className="text-text-hint">—</span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 font-medium text-text-secondary">Animais cadastrados</dt>
                <dd className="font-semibold text-text-title">{user.animalCount ?? 0}</dd>
              </div>
            </dl>

            <div className="mt-auto pt-6">
              <Button
                type="button"
                variant="danger"
                className="w-full"
                onClick={onDelete}
                disabled={isSaving}
              >
                <Trash2 size={16} />
                Excluir usuário
              </Button>
            </div>
          </aside>

          {/* Coluna direita — formulário */}
          <div className="flex-1 overflow-y-auto p-6">
            <form
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                onSave();
              }}
            >
              {/* Informações pessoais */}
              <div>
                <p className={sectionLabelClass}>Informações Pessoais</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Nome completo *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Telefone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => onFormDataChange({ ...formData, phone: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Cargo *</label>
                    <Select
                      value={formData.cargo}
                      onValueChange={(v) => onFormDataChange({ ...formData, cargo: v })}
                    >
                      <SelectTrigger className="h-[42px] w-full rounded-lg border border-border-strong bg-muted shadow-none">
                        <SelectValue placeholder="Selecione o cargo" />
                      </SelectTrigger>
                      <SelectContent>
                        {CARGO_OPTIONS.map((cargo) => (
                          <SelectItem key={cargo} value={cargo}>
                            {cargo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Propriedade */}
              <div className="border-t border-border pt-6">
                <p className={sectionLabelClass}>Propriedade</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Nome da propriedade *</label>
                    <input
                      type="text"
                      value={formData.property.name}
                      onChange={(e) => updateProperty({ name: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className={labelClass}>Cidade</label>
                      <input
                        type="text"
                        value={formData.property.city}
                        onChange={(e) => updateProperty({ city: e.target.value })}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>UF</label>
                      <input
                        type="text"
                        value={formData.property.uf}
                        onChange={(e) => updateProperty({ uf: e.target.value.toUpperCase() })}
                        className={inputClass}
                        maxLength={2}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Bloco de áreas */}
                <div className="mt-4 rounded-lg border border-border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex size-7 items-center justify-center rounded-lg bg-tertiary/10 text-tertiary">
                        <Building2 size={14} />
                      </span>
                      <p className="text-sm font-semibold text-text-title">Áreas (ha)</p>
                    </div>
                    <p className="text-sm text-text-secondary">
                      total <span className="font-semibold text-text-title">{areasTotal}</span> ha
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                      <label className={labelClass}>Pastagem</label>
                      <input
                        type="number"
                        value={formData.property.pastagem_ha || ''}
                        onChange={(e) =>
                          updateProperty({ pastagem_ha: parseFloat(e.target.value) || 0 })
                        }
                        className={inputClass}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Reserva</label>
                      <input
                        type="number"
                        value={formData.property.reserva_legal || ''}
                        onChange={(e) =>
                          updateProperty({ reserva_legal: parseFloat(e.target.value) || 0 })
                        }
                        className={inputClass}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Agrícola</label>
                      <input
                        type="number"
                        value={formData.property.area_agricola || ''}
                        onChange={(e) =>
                          updateProperty({ area_agricola: parseFloat(e.target.value) || 0 })
                        }
                        className={inputClass}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>ILPF</label>
                      <input
                        type="number"
                        value={formData.property.sistema_ilpf || ''}
                        onChange={(e) =>
                          updateProperty({ sistema_ilpf: parseFloat(e.target.value) || 0 })
                        }
                        className={inputClass}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type="button" onClick={onSave} disabled={isSaving}>
            {isSaving && <RefreshCw size={16} className="animate-spin" />}
            {isSaving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
